import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

import "./EstimatedEmissions.scss";

import {
	Button,
	COLORS,
	Label,
	SIZING,
	CheckboxInput,
} from "abs-ui-components";

import CustomTable from "../../../../common/customTable/CustomTable";
import Loader from "../../../../common/Loader/Loader";
import {
	EUETS_PORTSTAYS_COLUMNS,
	EUETS_VOYAGES_COLUMNS,
} from "../../../../constants/EUETS-Column-headers";
import { getEUETS } from "../../../../services/VesselEmissionsService";
import { ReportTimeSlots } from "../../../../services/ChildReportService";
import { accessAPIForResponse } from "../../../../services/api-services/api-implementations";
import { emitToasterMsg } from "../../../../services/toasterUtility";

const EstimatedEmissions = () => {
	const [EUETSVoyagesData, setEUETSVoyagesData] = useState([]);
	const [EUETSPortstaysData, setEUETSPortstaysData] = useState([]);
	const [timeSlots, setTimeSlots] = useState([]);
	const [startTimeSlot, setStartTimeSlot] = useState([]);
	const [endTimeSlot, setEndTimeSlot] = useState([]);
	const [selectedLink, setSelectedLink] = useState("First");
	const [fullYearDownloadData, setFullYearDownloadData] = useState(null);
	const [partialYearDownloadData, setPartialYearDownloadData] =
		useState(null);
	const [loading, setLoading] = useState(false);
	const reportDetails = useSelector(
		(state) => state?.reports?.selectedReportDetails
	);
	
	const reportYear = Number(reportDetails?.ReportYear) || null;
	const showTables = reportYear && reportYear < 2026;
	const shouldShowOnlyMessage = !loading && reportYear && reportYear >= 2026;
	const [payload, setPayload] = useState({
		startDate: null,
		endDate: null,
		startTime: "",
		endTime: "",
	});
	const [loader, setLoader] = useState(false);

	useEffect(() => {
		fetchEUETS();
	}, []);

	const fetchEUETS = async () => {
		try {
			setLoading(true);
			const EUETSResponse = await getEUETS(
				reportDetails?.ReportMstId
			).then((response) => response.data);
			setEUETSVoyagesData(EUETSResponse?.euEtsVoyagesDtls);
			setEUETSPortstaysData(EUETSResponse?.euEtsPortStaysDtls);
			setLoading(false);
		} catch (error) {
			console.error(error.message);
		} finally {
			setLoading(false);
		}
	};

	const fetchPartialEUETS = async () => {};

	const handleDateChange = (date, dateType) => {
		const selectedDate = dayjs(date);
		const request = {
			...payload,
			[dateType]: selectedDate.isValid() ? dayjs(date) : null,
		};
		setPayload({
			...payload,
			[dateType]: selectedDate.isValid() ? dayjs(date) : null,
		});
		const matchingSlot = timeSlots.find((slot) =>
			dayjs(slot.dateStamp).isSame(request[dateType], "day")
		);
		dateType === "startDate"
			? setStartTimeSlot(matchingSlot)
			: setEndTimeSlot(matchingSlot);
	};
	const handleTimeSlotChange = (event, timeType) => {
		setPayload({
			...payload,
			[timeType]: event.target.value,
		});
	};

	const linkChangeHandler = (e) => {
		setSelectedLink(e);
	};

	const reportTimeSlots = async () => {
		try {
			const responseData = await ReportTimeSlots(
				reportDetails.ReportMstId
			);

			if (responseData) {
				setTimeSlots(responseData.data.data);
			} else {
			}
		} catch (error) {
			console.error(error);
		}
	};

	const handleCheckboxChange = (id, type) => {
		if (type === "voyages") {
			if (fullYearDownloadData) {
				const voyagesDownloadList = fullYearDownloadData.Voyages;

				if (
					voyagesDownloadList &&
					voyagesDownloadList.length > 0 &&
					voyagesDownloadList.includes(id)
				) {
					const filterArray = voyagesDownloadList.filter(
						(e) => e !== id
					);
					setFullYearDownloadData({
						...fullYearDownloadData,
						Voyages: filterArray,
					});
				} else {
					if (voyagesDownloadList) {
						setFullYearDownloadData({
							...fullYearDownloadData,
							Voyages: [...voyagesDownloadList, id],
						});
					} else {
						setFullYearDownloadData({
							...fullYearDownloadData,
							Voyages: [id],
						});
					}
				}
			} else {
				setFullYearDownloadData({
					Voyages: [id],
				});
			}
		} else if (type === "portstays") {
			if (fullYearDownloadData) {
				const portstaysDownloadList = fullYearDownloadData.PortStays;

				if (
					portstaysDownloadList &&
					portstaysDownloadList.length > 0 &&
					portstaysDownloadList.includes(id)
				) {
					const filterArray = portstaysDownloadList.filter(
						(e) => e !== id
					);
					setFullYearDownloadData({
						...fullYearDownloadData,
						PortStays: filterArray,
					});
				} else {
					if (portstaysDownloadList) {
						setFullYearDownloadData({
							...fullYearDownloadData,
							PortStays: [...portstaysDownloadList, id],
						});
					} else {
						setFullYearDownloadData({
							...fullYearDownloadData,
							PortStays: [id],
						});
					}
				}
			} else {
				setFullYearDownloadData({
					PortStays: [id],
				});
			}
		} else if (type === "partial voyages") {
			if (partialYearDownloadData) {
				const voyagesDownloadList = partialYearDownloadData.Voyages;

				if (
					voyagesDownloadList &&
					voyagesDownloadList.length > 0 &&
					voyagesDownloadList.includes(id)
				) {
					const filterArray = voyagesDownloadList.filter(
						(e) => e !== id
					);
					setPartialYearDownloadData({
						...partialYearDownloadData,
						Voyages: filterArray,
					});
				} else {
					if (voyagesDownloadList) {
						setPartialYearDownloadData({
							...partialYearDownloadData,
							Voyages: [...voyagesDownloadList, id],
						});
					} else {
						setPartialYearDownloadData({
							...partialYearDownloadData,
							Voyages: [id],
						});
					}
				}
			} else {
				setPartialYearDownloadData({
					Voyages: [id],
				});
			}
		} else if (type === "partial portstays") {
			if (partialYearDownloadData) {
				const portstaysDownloadList = partialYearDownloadData.PortStays;

				if (
					portstaysDownloadList &&
					portstaysDownloadList.length > 0 &&
					portstaysDownloadList.includes(id)
				) {
					const filterArray = portstaysDownloadList.filter(
						(e) => e !== id
					);
					setPartialYearDownloadData({
						...partialYearDownloadData,
						PortStays: filterArray,
					});
				} else {
					if (portstaysDownloadList) {
						setPartialYearDownloadData({
							...partialYearDownloadData,
							PortStays: [...portstaysDownloadList, id],
						});
					} else {
						setPartialYearDownloadData({
							...partialYearDownloadData,
							PortStays: [id],
						});
					}
				}
			} else {
				setPartialYearDownloadData({
					PortStays: [id],
				});
			}
		}
	};

	const handleDownload = async () => {
		setLoader(true);
		const payLoad = {
			reportMstId: reportDetails?.ReportMstId,
			etsVoyageIds: fullYearDownloadData?.Voyages,
			etsPortStaysIds: fullYearDownloadData?.PortStays,
		};
		const response = await accessAPIForResponse(
			"GenerateEtsDocument",
			payLoad
		);
		if (response) {
			const url = window.URL.createObjectURL(new Blob([response]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", "ABS Statement.pdf");
			document.body.appendChild(link);
			link.click();
			setLoader(false);
			emitToasterMsg("success", "ABS Statement downloaded successfully.");
		} else {
			setLoader(false);
			emitToasterMsg("error", "Unable to download the ABS Statement.");
		}
	};

	const getPartialData = () => {
		fetchPartialEUETS();
	};

	useEffect(() => {
		// fetchGetReportSubmissionData();
		reportTimeSlots();
	}, [reportDetails.ReportMstId]);

	useEffect(() => {}, [
		payload.startDate,
		payload.endDate,
		payload.startTime,
		payload.endTime,
	]);

	return (
		<div className="EuEts">
			{loader && <Loader />}
			{showTables && (
				<div>
					<CustomTable
					columnsData={[
						...EUETS_VOYAGES_COLUMNS,
						{
							accessorKey:
								"selectVoyagesEntryForStatementDownload",
							header: "SELECT VOYAGES ENTRY FOR STATEMENT DOWNLOAD",
							size: 200,
							Cell: ({ row: { original } }) => {
								const isEnabled =
									original.portalVerificationStatus ===
										"Pass with warnings" ||
									original.portalVerificationStatus ===
										"Pass";
								return (
									<CheckboxInput
										disabled={!isEnabled}
										checked={
											fullYearDownloadData?.Voyages
												?.length > 0 &&
											fullYearDownloadData.Voyages.includes(
												original.uniqueId
											)
										}
										onChange={() =>
											handleCheckboxChange(
												original.uniqueId,
												"voyages"
											)
										}
									/>
								);
							},
						},
					]}
					bold={true}
						renderTableData={EUETSVoyagesData ?? []}
					customProps={{
						enableSort: false,
						columnResize: true,
						isLoading: loading,
						header: (
							<Label
								name="Voyages"
								size={SIZING.XLARGE}
								bold={true}
							></Label>
						),
						tableName: "EUETS",
						isLoading: loading,
					}}
					/>
				</div>
			)}
			{showTables && (
				<div className="mt-4">
					<CustomTable
					columnsData={[
						...EUETS_PORTSTAYS_COLUMNS,
						{
							accessorKey:
								"selectVoyagesEntryForStatementDownload",
							header: "SELECT VOYAGES ENTRY FOR STATEMENT DOWNLOAD",
							size: 200,
							Cell: ({ row: { original } }) => {
								const isEnabled =
									original.portalVerificationStatus ===
										"Pass with warnings" ||
									original.portalVerificationStatus ===
										"Pass";
								return (
									<CheckboxInput
										disabled={!isEnabled}
										checked={
											fullYearDownloadData?.PortStays
												?.length > 0 &&
											fullYearDownloadData.PortStays.includes(
												original.uniqueId
											)
										}
										onChange={() =>
											handleCheckboxChange(
												original.uniqueId,
												"portstays"
											)
										}
									/>
								);
							},
						},
					]}
						bold={true}
						renderTableData={EUETSPortstaysData ?? []}
						customProps={{
						enableSort: false,
						isLoading: loading,
						header: (
							<Label
								name="Port Stays"
								size={SIZING.XLARGE}
								bold={true}
							></Label>
						),
						tableName: "EUETS",
						isLoading: loading,
						}}
					/>
				</div>
			)}
			{!loading && showTables && (
				<div className="mt-3 d-flex justify-content-center align-items-center">
					<Button
						className="rounded"
						onClick={() => handleDownload()}
						disabled={
							!fullYearDownloadData ||
							((!fullYearDownloadData?.Voyages ||
								!fullYearDownloadData?.Voyages?.length > 0) &&
								(!fullYearDownloadData?.PortStays ||
									!fullYearDownloadData?.PortStays?.length > 0))
						}
					>
						Download ABS Statement for Selected Voyages
					</Button>
				</div>
			)}
			{!loading && (shouldShowOnlyMessage || showTables) && (
				<div className="mt-3 d-flex justify-content-center">
					<p style={{ color: 'black', textAlign: 'center', margin: 0, fontStyle: 'italic', maxWidth: '800px' }}>
						The ERP Statement of Fact is to be used for 2025 report year only for vessels using fossil fuels. For vessels using other than fossil fuels (such as LNG and biofuels), and for 2026 reporting year, contact your local ABS.
					</p>
				</div>
			)}
		</div>
	);
};

export default EstimatedEmissions;
